import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { Box, Typography, alpha, useMediaQuery, useTheme } from "@mui/material";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, } from "chart.js";
import { useTranslation } from "react-i18next";
import { NodeLogCard } from "./NodeLogCard";
import { buildTemperatureChartSeries, } from "./temperatureChartSeries";
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);
const MAX_X_LABELS = 12;
/** Width of the y-axis label column plus its gap to the plot (Figma: 24 + 20). */
const Y_AXIS_WIDTH = 44;
const Y_LABEL_GAP = 20;
/** Height of the x-axis label row plus its gap to the plot (Figma: 12 + 18). */
const X_AXIS_HEIGHT = 30;
const X_LABEL_GAP = 12;
const CHART_HEIGHT = 412;
const CHART_HEIGHT_MOBILE = 300;
const AXIS_FONT = { family: "Outfit", size: 12, lineHeight: 1.5 };
const SERIES_META = {
    internalTemperature: {
        unit: "°C",
        tooltipUnit: " °C",
        valueKey: "temperature",
        emptyTicks: [85, 50, 30, 10, 0],
    },
    externalTemperature: {
        unit: "°C",
        tooltipUnit: " °C",
        valueKey: "externalTemperature",
        emptyTicks: [85, 50, 30, 10, 0],
    },
    humidity: {
        unit: "%",
        tooltipUnit: "%",
        valueKey: "humidity",
        emptyTicks: [100, 75, 50, 25, 0],
    },
    co2: {
        unit: " ppm",
        tooltipUnit: " ppm",
        valueKey: "co2",
        emptyTicks: [2000, 1500, 1000, 500, 0],
    },
};
/** Y-axis label and grid line colours: the same grey for every series (temperature, humidity, CO2) and every node type. */
const AXIS_COLORS = { label: "#919EAB", line: "#919EAB" };
const getStepSize = (span) => {
    if (span <= 2)
        return 0.5;
    if (span <= 5)
        return 1;
    if (span <= 10)
        return 2;
    return Math.max(Math.ceil(span / 5), 1);
};
/** Series switch (Figma: ToggleButtonGroup, 44px tall, 36px pills). */
const SeriesToggle = ({ value, keys, labels, onChange, }) => (React.createElement(Box, { role: "group", sx: {
        display: "flex",
        gap: "4px",
        p: "4px",
        maxWidth: "100%",
        overflowX: "auto",
        borderRadius: "56px",
        backgroundColor: "#F4F6F8",
        boxShadow: (theme) => `inset 0 0 0 1px ${alpha(theme.palette.grey[500], 0.08)}`,
    } }, keys.map((key) => {
    const active = key === value;
    return (React.createElement(Box, { key: key, component: "button", type: "button", "aria-pressed": active, onClick: () => onChange(key), sx: {
            flexShrink: 0,
            height: 36,
            px: 1,
            border: 0,
            cursor: "pointer",
            borderRadius: "56px",
            fontFamily: "inherit",
            fontSize: 14,
            lineHeight: "22px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            backgroundColor: active
                ? (theme) => alpha(theme.palette.primary.main, 0.08)
                : "transparent",
            color: active ? "primary.darker" : "text.secondary",
            "&:hover": {
                backgroundColor: (theme) => alpha(theme.palette.grey[500], active ? 0.12 : 0.08),
            },
        } }, labels[key]));
})));
/** Axis skeleton shown when there is nothing to plot (Figma: EmptyLog State graph). */
const EmptyChart = ({ height, message, meta }) => (React.createElement(Box, { sx: { position: "relative", height } },
    React.createElement(Box, { sx: {
            position: "absolute",
            inset: 0,
            bottom: X_AXIS_HEIGHT,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
        } }, meta.emptyTicks.map((tick) => {
        const band = AXIS_COLORS;
        return (React.createElement(Box, { key: tick, sx: { display: "flex", alignItems: "center", height: 18 } },
            React.createElement(Typography, { sx: {
                    width: Y_AXIS_WIDTH - Y_LABEL_GAP,
                    flexShrink: 0,
                    fontSize: 12,
                    lineHeight: "18px",
                    color: band.label,
                    whiteSpace: "nowrap",
                } },
                tick,
                meta.unit),
            React.createElement(Box, { sx: {
                    flex: 1,
                    ml: `${Y_LABEL_GAP}px`,
                    borderTop: `1px dashed ${alpha(band.line, 0.4)}`,
                } })));
    })),
    React.createElement(Box, { sx: {
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
        } },
        React.createElement(Typography, { variant: "body2" }, message))));
/**
 * Chart card for the new node-log layout. Each series plots one field of the log response;
 * a series with no values (e.g. a node without that sensor) shows the empty state.
 */
export const NodeLogChartCard = ({ logs, title, seriesKeys, }) => {
    const { t } = useTranslation("common");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [selectedSeries, setSeries] = useState("internalTemperature");
    // The offered series can change with the node; fall back to the first one on a mismatch.
    const series = seriesKeys.includes(selectedSeries)
        ? selectedSeries
        : seriesKeys[0];
    const meta = SERIES_META[series];
    const containerRef = useRef(null);
    const [chartWidthPx, setChartWidthPx] = useState(800);
    const measure = useCallback(() => {
        var _a, _b;
        const width = (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width) !== null && _b !== void 0 ? _b : 0;
        if (width > 0)
            setChartWidthPx(Math.floor(width));
    }, []);
    useEffect(() => {
        measure();
        const observer = new ResizeObserver(() => measure());
        if (containerRef.current)
            observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [measure]);
    const points = useMemo(() => {
        if (series === "internalTemperature") {
            return buildTemperatureChartSeries(logs, chartWidthPx, isMobile);
        }
        // Project the series' field onto `temperature` so the shared builder filters and buckets it.
        const projected = logs.map((l) => {
            var _a;
            return ({
                dateTime: l.dateTime,
                temperature: (_a = l[meta.valueKey]) !== null && _a !== void 0 ? _a : null,
            });
        });
        return buildTemperatureChartSeries(projected, chartWidthPx, isMobile);
    }, [series, meta.valueKey, logs, chartWidthPx, isMobile]);
    const values = useMemo(() => {
        const result = [];
        for (const p of points) {
            for (const v of [p.temperature, p.minTemperature, p.maxTemperature]) {
                if (v != null && !Number.isNaN(Number(v)))
                    result.push(Number(v));
            }
        }
        return result;
    }, [points]);
    const labels = {
        internalTemperature: t("labels.internalTemperature"),
        externalTemperature: t("labels.externalTemperature"),
        humidity: t("labels.humidity"),
        co2: t("labels.co2"),
    };
    const height = isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT;
    const hasData = values.length > 0;
    const rawMin = hasData ? Math.min(...values) : 0;
    const rawMax = hasData ? Math.max(...values) : 1;
    const pad = Math.max(0.5, Math.min((rawMax - rawMin) * 0.05, 2));
    let yMin = hasData ? Math.floor((rawMin - pad) * 10) / 10 : 0;
    // Humidity and CO2 cannot be negative; only temperature series may go below zero.
    if (series !== "internalTemperature" && series !== "externalTemperature")
        yMin = Math.max(0, yMin);
    let yMax = hasData ? Math.ceil((rawMax + pad) * 10) / 10 : 1;
    if (yMin === yMax) {
        yMin -= 0.5;
        yMax += 0.5;
    }
    // Whole-number axis (like the design's 85 / 50 / 30 / 10 / 0) whenever the span allows it.
    const yStep = getStepSize(yMax - yMin);
    if (yStep >= 1) {
        yMin = Math.floor(yMin / yStep) * yStep;
        yMax = Math.ceil(yMax / yStep) * yStep;
    }
    const xLabelCount = isMobile ? 6 : MAX_X_LABELS;
    const chartLabels = points.map((p) => p.month);
    const chartData = {
        labels: chartLabels,
        datasets: [
            {
                data: points.map((p) => p.temperature),
                borderColor: "#1D95E2",
                borderWidth: 2,
                spanGaps: true,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointHitRadius: 14,
                pointHoverBackgroundColor: "#1D95E2",
                pointHoverBorderColor: "#FFFFFF",
                pointHoverBorderWidth: 2,
                tension: 0,
                fill: false,
            },
        ],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: "index" },
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: "rgba(0, 0, 0, 0.82)",
                titleColor: "#FFFFFF",
                bodyColor: "#FFFFFF",
                borderColor: "#1D95E2",
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: false,
                callbacks: {
                    title: (items) => {
                        var _a, _b;
                        const p = points[(_b = (_a = items[0]) === null || _a === void 0 ? void 0 : _a.dataIndex) !== null && _b !== void 0 ? _b : 0];
                        if (p === null || p === void 0 ? void 0 : p.dateTimeIso) {
                            try {
                                return new Date(p.dateTimeIso).toLocaleString(undefined, {
                                    hour12: false,
                                });
                            }
                            catch (_c) {
                                return p.month || "";
                            }
                        }
                        return (p === null || p === void 0 ? void 0 : p.month) || "";
                    },
                    label: (ctx) => {
                        const p = points[ctx.dataIndex];
                        if (!p || p.temperature == null)
                            return "";
                        if (p.isAggregated && p.minTemperature != null && p.maxTemperature != null) {
                            return [
                                `Average: ${p.temperature}${meta.tooltipUnit}`,
                                `Min: ${p.minTemperature}${meta.tooltipUnit}`,
                                `Max: ${p.maxTemperature}${meta.tooltipUnit}`,
                            ];
                        }
                        return `${labels[series]}: ${p.temperature}${meta.tooltipUnit}`;
                    },
                },
            },
        },
        layout: { padding: { top: 8, bottom: 0, left: 0, right: 0 } },
        scales: {
            x: {
                // No tick marks: chart.js still reserves the 8px tick length when the grid is hidden, which
                // pushes the labels 8px below the fixed axis height and cuts them off.
                grid: { display: false, drawTicks: false },
                border: { display: false },
                afterFit: (scale) => {
                    scale.height = X_AXIS_HEIGHT;
                },
                ticks: {
                    autoSkip: true,
                    maxTicksLimit: xLabelCount,
                    maxRotation: 0,
                    padding: X_LABEL_GAP,
                    color: "#919EAB",
                    font: AXIS_FONT,
                },
            },
            y: {
                min: yMin,
                max: yMax,
                border: { display: false },
                // Wide enough for the longest label (decimal ticks are wider than the design's integers).
                afterFit: (scale) => {
                    scale.ctx.save();
                    scale.ctx.font = `${AXIS_FONT.size}px ${AXIS_FONT.family}`;
                    const widest = Math.max(0, ...scale.ticks.map((tick) => { var _a; return scale.ctx.measureText(String((_a = tick.label) !== null && _a !== void 0 ? _a : "")).width; }));
                    scale.ctx.restore();
                    scale.width = Math.max(Y_AXIS_WIDTH, Math.ceil(widest) + Y_LABEL_GAP);
                },
                grid: {
                    // No tick marks: they would add 8px between the label and the plot.
                    drawTicks: false,
                    lineWidth: 1,
                    tickBorderDash: [4, 4],
                    color: alpha(AXIS_COLORS.line, 0.4),
                },
                ticks: {
                    padding: Y_LABEL_GAP,
                    stepSize: yStep,
                    font: AXIS_FONT,
                    callback: (value) => `${value}${meta.unit}`,
                    color: AXIS_COLORS.label,
                },
            },
        },
    };
    return (React.createElement(NodeLogCard, { title: title, actions: seriesKeys.length > 1 ? (React.createElement(SeriesToggle, { value: series, keys: seriesKeys, labels: labels, onChange: setSeries })) : undefined },
        React.createElement(Box, { ref: containerRef, sx: { width: "100%", height, position: "relative" } }, hasData ? (React.createElement(Line, { data: chartData, options: options })) : (React.createElement(EmptyChart, { height: height, message: t("status.noData"), meta: meta })))));
};
